import { AsyncPipe } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import {
  AppState,
  DocumentTemplatesActions,
  ItemStore,
  ProductSchemasStore,
  SchemaEditorActions,
  selectMyDealsWorkflowsGrouped,
  selectMyDealsWorkflowsLoading,
  selectSchemaViewBySid,
  WorkflowActions,
} from '@qtek/libs/store';
import { TemplateSelectorComponent } from '@qtek/libs/templates';
import {
  TranslationCoreModule,
  TranslationCoreService,
} from '@qtek/libs/translation-core';
import {
  BaseEnumValue,
  DocumentTemplate,
  Item,
  ItemTypeBitMasks,
  NDA_WORKFLOW_OPTIONS,
  NdaWorkflow,
  TemplateTypes,
} from '@qtek/shared/models';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  forkJoin,
  map,
  of,
  startWith,
  switchMap,
  take,
} from 'rxjs';
import {
  TermSheetExtractionResult,
  TermSheetReviewField,
  TermSheetReviewSection,
} from '../current-deal-entry-method-dialog/term-sheet-extraction.utils';

export const termSheetReviewDialogConfig: MatDialogConfig = {
  panelClass: ['qt-dialog', 'term-sheet-review-dialog'],
  width: '860px',
  maxWidth: '95vw',
  maxHeight: '90vh',
  disableClose: true,
};

interface EditableField extends TermSheetReviewField {
  editing: boolean;
  draft: string;
}

interface ReviewSectionVM {
  title: string;
  fields: EditableField[];
}

interface SubFacilityVM {
  name: string | null;
  productType: string | null;
  industry: string | null;
  sections: ReviewSectionVM[];
  raw: Record<string, string | null>;
  fieldCount: number;
}

interface SchemaEnumItem extends BaseEnumValue {
  id: string;
  label?: string;
  description?: string;
  name?: string;
}

interface DialogData {
  extraction: TermSheetExtractionResult;
  fileName?: string;
  // SD-2150: docId of the uploaded source document, threaded through to the
  // create payload so the BE links it to the new deal (data-room visibility +
  // covenant extraction discoverability).
  docId?: string;
}

const SID_AGENCY = 'io.synply.v1.model.core.agency';
const SID_COMMON = 'io.synply.v1.model.core.common';
const SID_FEES = 'io.synply.v1.model.core.fees';
const SID_BORROWER = 'io.synply.v1.model.core.borrower';
const SID_COLLATERAL = 'io.synply.v1.model.core.collateral';
const SID_GUARANTORS = 'io.synply.v1.model.core.guarantors';
const SID_PRODUCT_TERM = 'io.synply.v1.model.product.term';
const SID_PRODUCT_DELAYED_DRAW_TERM =
  'io.synply.v1.model.product.delayedDrawTerm';
const SID_PARTICIPATION = 'io.synply.v1.model.derived.participation';

const CREATE_DEAL_MYSID = 'create_deal';

@Component({
  selector: 'qt-term-sheet-review-dialog',
  templateUrl: './term-sheet-review-dialog.component.html',
  styleUrls: ['./term-sheet-review-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    TemplateSelectorComponent,
    TranslationCoreModule,
  ],
})
export class TermSheetReviewDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<TermSheetReviewDialogComponent>);
  private data = inject<DialogData>(MAT_DIALOG_DATA);
  private store = inject(Store<AppState>);
  private readonly itemStore = inject(ItemStore);
  private readonly schemasStore = inject(ProductSchemasStore);
  private readonly injector = inject(Injector);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslationCoreService);
  private http = inject(HttpClient);

  readonly fileName = this.data.fileName ?? 'term-sheet.pdf';
  readonly fieldCount = this.data.extraction.fieldCount;
  readonly subFacilities: SubFacilityVM[] = (
    this.data.extraction.subFacilities ?? []
  ).map(sf => ({
    name: sf.name,
    productType: sf.productType,
    industry: sf.industry,
    sections: sf.sections.map(section => ({
      title: section.title,
      fields: section.fields.map(field => ({
        ...field,
        editing: false,
        draft: field.value,
      })),
    })),
    raw: sf.raw,
    fieldCount: sf.fieldCount,
  }));
  readonly hasSubFacilities = this.subFacilities.length > 0;
  readonly totalFieldCount =
    this.data.extraction.fieldCount +
    this.subFacilities.reduce((sum, sf) => sum + sf.fieldCount, 0);

  readonly ndaWorkflowOptions = NDA_WORKFLOW_OPTIONS;
  readonly templateTypes = TemplateTypes;
  readonly showNdaTemplate = signal(false);
  readonly saving = signal(false);

  readonly dealName = signal<string>(this.data.extraction.dealName ?? '');
  readonly productOptions = signal<SchemaEnumItem[]>([]);
  readonly industryOptions = signal<SchemaEnumItem[]>([]);
  readonly termSheetSelectOptions = signal<Record<string, SchemaEnumItem[]>>(
    {}
  );

  readonly form = new FormGroup({
    product: new FormControl<string | null>(null, Validators.required),
    industry: new FormControl<string | null>(null, Validators.required),
    ndaWorkflow: new FormControl<string>(NdaWorkflow.Personal, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    ndaTemplate: new FormControl<DocumentTemplate | null>(null),
    folderTemplate: new FormControl<DocumentTemplate | null>(null),
    workflowTemplate: new FormControl<string[]>([], { nonNullable: true }),
  });

  readonly workflowTemplateGroups$ = this.store.select(
    selectMyDealsWorkflowsGrouped
  );
  readonly workflowTemplatesLoading$ = this.store.select(
    selectMyDealsWorkflowsLoading
  );

  sections: ReviewSectionVM[] = this.data.extraction.sections.map(section => ({
    title: section.title,
    fields: section.fields.map(field => ({
      ...field,
      editing: false,
      draft: field.value,
    })),
  }));

  ngOnInit(): void {
    this.store.dispatch(
      DocumentTemplatesActions.connectDocumentTemplates({
        payload: { view: 'dtl' },
      })
    );
    this.store.dispatch(WorkflowActions.subscribeMyDealsWorkflows());

    this.itemStore.connect({
      op: 'fast_query',
      mysid: CREATE_DEAL_MYSID,
      prms: { fastView: 'itemsView' },
      q: [{ k: 'stg', op: 'NQ', v: [500, 600] }],
    });

    this.loadProductOptions();
    this.loadIndustryOptions();
    this.watchNdaWorkflow();
    this.markRequiredTouched();
    this.setupDropdownOptions();
  }

  private markRequiredTouched(): void {
    const c = this.form.controls;
    if (!c.product.value) c.product.markAsTouched();
    if (!c.industry.value) c.industry.markAsTouched();
    if (!c.ndaTemplate.value) c.ndaTemplate.markAsTouched();
  }

  ngOnDestroy(): void {
    this.store.dispatch(DocumentTemplatesActions.disconnectDocumentTemplates());
    this.store.dispatch(WorkflowActions.unsubscribeMyDealsWorkflows());
    this.itemStore.disconnect(CREATE_DEAL_MYSID);
  }

  // ─── Schema loading & prefill ─────────────────────────────────

  private loadProductOptions(): void {
    this.store.dispatch(
      SchemaEditorActions.getSchema({ sid: 'io.synply.v1.type.product' })
    );
    this.store
      .pipe(
        select(selectSchemaViewBySid('io.synply.v1.type.product')),
        filter(schema => !!schema),
        first(),
        map(
          schema =>
            Object.entries(schema?.graphEnum?.vals ?? {}).map(([id, val]) => ({
              id,
              ...(val as Record<string, unknown>),
            })) as SchemaEnumItem[]
        ),
        switchMap(res => {
          const labels = res.map(i => i.label).filter(Boolean) as string[];
          if (labels.length === 0) return [res];
          return this.translateService.getTranslations(labels).pipe(
            take(1),
            map(translations =>
              res.map(i => ({
                ...i,
                name: translations[i.label as string] || i.description,
              }))
            )
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(options => {
        this.productOptions.set(options);
        const match = this.findSchemaMatch(
          this.data.extraction.productType,
          options
        );
        if (match) {
          this.form.controls.product.setValue(match.id);
        }
        this.cdr.markForCheck();
      });
  }

  private loadIndustryOptions(): void {
    this.store.dispatch(
      SchemaEditorActions.getSchema({ sid: 'io.synply.v1.type.indu' })
    );
    this.store
      .pipe(
        select(selectSchemaViewBySid('io.synply.v1.type.indu')),
        filter(schema => !!schema),
        first(),
        map(
          schema =>
            Object.entries(schema?.graphEnum?.vals ?? {}).map(([id, val]) => ({
              id,
              ...(val as Record<string, unknown>),
            })) as SchemaEnumItem[]
        ),
        switchMap(res => {
          const labels = res.map(i => i.label).filter(Boolean) as string[];
          if (labels.length === 0) return [res];
          return this.translateService.getTranslations(labels).pipe(
            take(1),
            map(translations =>
              res.map(i => ({
                ...i,
                name: translations[i.label as string] || i.description,
              }))
            )
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(options => {
        this.industryOptions.set(options);
        const match = this.findSchemaMatch(
          this.data.extraction.industry,
          options
        );
        if (match) {
          this.form.controls.industry.setValue(match.id);
        }
        this.cdr.markForCheck();
      });
  }

  private normalize(value: unknown): string {
    if (value == null) return '';
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Resolve an LLM-extracted free-text value (e.g. "Revolver", "Healthcare")
   * to a schema enum option id by fuzzy label/description matching.
   */
  private findSchemaMatch(
    raw: string | null,
    options: SchemaEnumItem[]
  ): SchemaEnumItem | null {
    if (!raw || options.length === 0) return null;
    const target = this.normalize(raw);
    if (!target) return null;

    // 1. Exact id match (LLM returned a schema id, unusual but possible)
    const idMatch = options.find(o => this.normalize(o.id) === target);
    if (idMatch) return idMatch;

    // 2. Label / name / description equality
    const exact = options.find(o =>
      [o.label, o.name, o.description].some(
        txt => txt && this.normalize(txt) === target
      )
    );
    if (exact) return exact;

    // 3. Substring match (either direction) — last-resort fuzz
    const fuzzy = options.find(o =>
      [o.label, o.name, o.description].some(txt => {
        if (!txt) return false;
        const n = this.normalize(txt);
        return (
          (n.length > 3 && n.includes(target)) ||
          (target.length > 3 && target.includes(n))
        );
      })
    );
    return fuzzy ?? null;
  }

  // ─── NDA workflow wiring ──────────────────────────────────────

  private watchNdaWorkflow(): void {
    this.form.controls.ndaWorkflow.valueChanges
      .pipe(
        startWith(this.form.controls.ndaWorkflow.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        const template = this.form.controls.ndaTemplate;
        if (value === NdaWorkflow.Personal) {
          template.setValidators([Validators.required]);
          this.showNdaTemplate.set(true);
        } else {
          template.clearValidators();
          template.setValue(null);
          this.showNdaTemplate.set(false);
        }
        template.updateValueAndValidity();
        this.cdr.markForCheck();
      });
  }

  // ─── Inline editing ───────────────────────────────────────────

  startEdit(field: EditableField): void {
    const options = this.termSheetSelectOptions()[field.key];
    if (options?.length) {
      const match = this.findSchemaMatch(field.value, options);
      field.draft = match?.id ?? field.value;
    } else if (field.type === 'percent') {
      field.draft = field.value.replaceAll('%', '').trim();
    } else {
      field.draft = field.value;
    }
    field.editing = true;
    this.cdr.markForCheck();
  }

  commitEdit(field: EditableField): void {
    if (!field.editing) return;
    field.value = field.draft.trim();
    field.editing = false;
    this.cdr.markForCheck();
  }

  cancelEdit(field: EditableField): void {
    field.draft = field.value;
    field.editing = false;
    this.cdr.markForCheck();
  }

  onEditKeydown(event: KeyboardEvent, field: EditableField): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitEdit(field);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit(field);
    }
  }

  // ─── Submit ───────────────────────────────────────────────────

  cancel(): void {
    this.dialogRef.close();
  }

  get canSubmit(): boolean {
    return !!this.dealName().trim() && this.form.valid && !this.saving();
  }

  submit(): void {
    if (this.saving()) return;

    const dealName = this.dealName().trim();
    if (!dealName) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const {
      product,
      industry,
      ndaWorkflow,
      ndaTemplate,
      folderTemplate,
      workflowTemplate,
    } = this.form.getRawValue();
    const productType = product ?? '';
    const industryId = industry ?? '';

    const ndaDtaId = this.extractTemplateId(ndaTemplate);
    const ndaTplId = ndaTemplate?._id ?? null;
    const folderTemplateStructureId = this.extractTemplateId(folderTemplate);
    const templateWorkflowIds = workflowTemplate ?? [];

    const parent = this.buildItemPayload({
      name: dealName,
      rawExtraction: this.data.extraction.raw,
      editedExtractionPayload: this.buildExtractionPayload(),
      productType,
      industryId,
      nda: {
        workflow: ndaWorkflow,
        dtaId: ndaDtaId,
        tplId: ndaTplId,
      },
      templateStructureId: folderTemplateStructureId,
      templateWorkflowIds,
    });

    // SD-2150: link the uploaded source doc to the new deal (parent only) so it
    // lands in the data room and its covenant extraction is discoverable.
    if (this.data.docId) {
      parent.srcDocId = this.data.docId;
    }

    if (this.hasSubFacilities) {
      const subItems = this.subFacilities.map((sf, idx) => {
        const subProductId =
          this.findSchemaMatch(sf.productType, this.productOptions())?.id ||
          productType;
        const subIndustryId =
          this.findSchemaMatch(sf.industry, this.industryOptions())?.id ||
          industryId;
        return this.buildItemPayload({
          name: sf.name?.trim() || `${dealName} — Facility ${idx + 2}`,
          rawExtraction: sf.raw,
          editedExtractionPayload: this.buildSubFacilityPayload(sf),
          productType: subProductId,
          industryId: subIndustryId,
          nda: {
            workflow: ndaWorkflow,
            dtaId: ndaDtaId,
            tplId: ndaTplId,
          },
        });
      });

      (parent as unknown as { subItems: unknown[] }).subItems = subItems;

      this.saving.set(true);
      this.http
        .post<{ res?: Array<{ _id?: string; type?: string }> }>(
          '/api/v1/service/item-bulk-upsert',
          { deals: [parent] },
          { withCredentials: true }
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.saving.set(false);
            this.dialogRef.close();
            const created = response?.res?.[0];
            const parentId = created?._id;
            if (parentId) {
              const isAgency =
                created?.type?.toLowerCase?.()?.includes('agency') ?? false;
              const url = isAgency ? 'itms/agency' : 'itms/originations';
              this.router.navigate([url, 'deal', btoa(parentId)], {
                queryParams: { prevUrl: this.router.url },
              });
            }
          },
          error: () => {
            this.saving.set(false);
            this.cdr.markForCheck();
          },
        });
      return;
    }

    this.saving.set(true);

    this.itemStore.create$
      .pipe(
        filter(
          ({ res, mysid }) =>
            mysid === CREATE_DEAL_MYSID && res.name === dealName
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ res }) => {
        this.itemStore.disconnect(CREATE_DEAL_MYSID);
        this.saving.set(false);
        this.dialogRef.close();
        this.router.navigate(['itms/originations', 'deal', btoa(res._id)], {
          queryParams: { prevUrl: this.router.url },
        });
      });

    this.itemStore.createItem(parent);
  }

  private buildItemPayload(opts: {
    name: string;
    rawExtraction: Record<string, string | null>;
    editedExtractionPayload: Record<string, unknown>;
    productType: string;
    industryId: string;
    nda?: {
      workflow: string;
      dtaId: string | null;
      tplId: string | null;
    };
    templateStructureId?: string | null;
    templateWorkflowIds?: string[];
  }): Item {
    const remapped = this.remapAliases(opts.editedExtractionPayload);
    const originalAmount = this.parseNumber(remapped['c.oAmt']);

    const item: Item = new Item();
    item.name = opts.name;
    item.prTp = ItemTypeBitMasks.LOAN;
    if (opts.nda) {
      item.ndaWorkFlow = opts.nda.workflow;
      if (opts.nda.dtaId) item.ndaDtaId = opts.nda.dtaId;
      if (opts.nda.tplId) item.ndaTplId = opts.nda.tplId;
    }
    if (opts.templateStructureId) {
      item.templateStructureId = opts.templateStructureId;
    }
    if (opts.templateWorkflowIds && opts.templateWorkflowIds.length > 0) {
      item.templateWorkflowIds = opts.templateWorkflowIds;
    }
    item.myPar = {
      sts: 7,
      role: 1000,
      committedVal: { a: originalAmount ?? 0, c: 'USD' },
    };
    item.data = {
      sid: {
        a: SID_AGENCY,
        c: SID_COMMON,
        f: SID_FEES,
        b: SID_BORROWER,
        col: SID_COLLATERAL,
        g: SID_GUARANTORS,
        l: SID_PRODUCT_TERM,
        d: SID_PRODUCT_DELAYED_DRAW_TERM,
        s: opts.productType,
        i: opts.industryId,
        p: SID_PARTICIPATION,
      },
      data: {
        ...remapped,
        'c.pr': opts.productType,
        'c.indu': opts.industryId,
        ...(originalAmount == null ? {} : { 'c.oAmt': originalAmount }),
      },
    };
    return item;
  }

  private buildRawPayload(
    raw: Record<string, string | null>
  ): Record<string, unknown> {
    const base: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(raw ?? {})) {
      if (key === 'isValid' || key === 'dealName' || key === 'name') continue;
      if (value == null) continue;
      const trimmed = String(value).trim();
      if (!trimmed || trimmed.toLowerCase() === 'null') continue;
      base[key] = trimmed;
    }
    return base;
  }

  private buildSubFacilityPayload(sf: SubFacilityVM): Record<string, unknown> {
    return this.applyFieldEdits(this.buildRawPayload(sf.raw), sf.sections);
  }

  /**
   * Build the flat `{ 'c.pr': ..., 'c.oAmt': ..., ... }` payload from the
   * extracted raw data + user edits. Values are re-parsed per field type.
   * Non-user fields (`isValid`, `dealName`) are filtered out — they live on
   * `item.name` / are control flags, not data points.
   */
  private buildExtractionPayload(): Record<string, unknown> {
    return this.applyFieldEdits(
      this.buildRawPayload(this.data.extraction.raw ?? {}),
      this.sections,
      'dealName'
    );
  }

  /**
   * Applies user-edited field values from the review sections onto `base`,
   * overwriting raw values. Entries whose parsed value is empty or null are
   * removed from `base`. Pass `skipKey` to ignore a specific field key (e.g.
   * `'dealName'`, which is stored on `item.name` instead).
   */
  private applyFieldEdits(
    base: Record<string, unknown>,
    sections: ReviewSectionVM[],
    skipKey?: string
  ): Record<string, unknown> {
    for (const section of sections) {
      for (const field of section.fields) {
        if (skipKey && field.key === skipKey) continue;
        const raw = field.value?.trim() ?? '';
        if (!raw) {
          delete base[field.key];
          continue;
        }
        const parsed = this.parseFieldValue(raw, field.type);
        if (parsed === null) {
          delete base[field.key];
          continue;
        }
        base[field.key] = parsed;
      }
    }
    return base;
  }

  /**
   * Backend extractor emits aliases `c1` (collateral), `t` (product.term).
   * We submit with the project-convention aliases `col` and `l` to match
   * closed-deal-stepper. Rewrite keys accordingly.
   */
  private remapAliases(
    payload: Record<string, unknown>
  ): Record<string, unknown> {
    const remap: Record<string, string> = { c1: 'col', t: 'l' };
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(payload)) {
      const dot = key.indexOf('.');
      if (dot < 0) {
        out[key] = value;
        continue;
      }
      const alias = key.slice(0, dot);
      const rest = key.slice(dot + 1);
      const mapped = remap[alias] ?? alias;
      out[`${mapped}.${rest}`] = value;
    }
    return out;
  }

  private parseFieldValue(
    raw: string,
    type: TermSheetReviewField['type']
  ): string | number | null {
    switch (type) {
      case 'number': {
        const n = Number(raw.replace(/,/g, ''));
        return Number.isFinite(n) ? n : null;
      }
      case 'percent': {
        const n = Number(raw.replace(/%/g, '').replace(/,/g, '').trim());
        // Backend schemas with format=percentage expect decimals (0.34 for 34%).
        return Number.isFinite(n) ? n / 100 : null;
      }
      case 'date':
        return raw;
      default:
        return raw;
    }
  }

  private parseNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n =
      typeof value === 'number'
        ? value
        : Number(String(value).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }

  /**
   * Mirror of closed-deal-stepper-dialog.component.ts:970 — resolves the
   * versioned template reference the backend expects for `item.ndaDtaId`.
   */
  private extractTemplateId(template: DocumentTemplate | null): string | null {
    if (!template) return null;

    let templateData = template.history?.[0];

    if (!templateData && template.data && template.data.length > 0) {
      templateData = template.data[0];
    }

    if (templateData?._id && templateData._vts) {
      return `${templateData._id}.${templateData._vts}`;
    }

    if (template._id) {
      const latestVersionData = template.dataMeta?.templateData?.[0];
      if (latestVersionData) {
        return `${latestVersionData.id}.${latestVersionData.vts}`;
      }
    }

    return null;
  }

  trackField(_: number, field: TermSheetReviewField): string {
    return field.key;
  }

  trackSection(
    _: number,
    section: TermSheetReviewSection | ReviewSectionVM
  ): string {
    return section.title;
  }

  displayFieldValue(field: EditableField): string {
    if (field.key === 'dealName') {
      return this.dealName() || field.value;
    }
    if (field.key === 'c.pr') {
      return this.resolveSchemaLabel(
        this.form.controls.product.value ?? field.value,
        this.productOptions()
      );
    }
    if (field.key === 'c.indu') {
      return this.resolveSchemaLabel(
        this.form.controls.industry.value ?? field.value,
        this.industryOptions()
      );
    }
    return this.resolveFieldDisplay(field);
  }

  private resolveFieldDisplay(field: EditableField): string {
    const options = this.termSheetSelectOptions()[field.key];
    if (options?.length) {
      const match =
        options.find(o => o.id === field.value) ??
        this.findSchemaMatch(field.value, options);
      if (match) return match.name || match.label || field.value;
    }
    if (field.type === 'percent' && field.value) {
      const clean = field.value.replaceAll('%', '').trim();
      return clean ? `${clean}%` : field.value;
    }
    return field.value;
  }

  private resolveSchemaLabel(
    id: string | null,
    options: SchemaEnumItem[]
  ): string {
    if (!id) return '';
    const match = options.find(o => o.id === id);
    if (match) return match.name || match.label || this.humanizeSchemaId(id);
    return this.humanizeSchemaId(id);
  }

  private humanizeSchemaId(raw: string): string {
    if (!raw) return '';
    const lastSegment = raw.split('.').pop() || raw;
    const spaced = lastSegment.replace(/([A-Z])/g, ' $1').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  isEditable(field: EditableField): boolean {
    return (
      field.key !== 'dealName' && field.key !== 'c.pr' && field.key !== 'c.indu'
    );
  }

  trackSubFacility(index: number, sf: SubFacilityVM): string {
    return `${index}-${sf.name ?? ''}`;
  }

  displaySubFacilityValue(field: EditableField, sf: SubFacilityVM): string {
    if (field.key === 'c.pr') {
      return this.resolveSchemaLabel(sf.productType, this.productOptions());
    }
    if (field.key === 'c.indu') {
      return this.resolveSchemaLabel(sf.industry, this.industryOptions());
    }
    return this.resolveFieldDisplay(field);
  }

  getFieldOptions(field: EditableField): SchemaEnumItem[] {
    return this.termSheetSelectOptions()[field.key] ?? [];
  }

  // ─── Dropdown options loading ─────────────────────────────────

  private setupDropdownOptions(): void {
    combineLatest([
      this.form.controls.product.valueChanges.pipe(
        startWith(this.form.controls.product.value)
      ),
      this.form.controls.industry.valueChanges.pipe(
        startWith(this.form.controls.industry.value)
      ),
    ])
      .pipe(
        filter(([product, industry]) => Boolean(product && industry)),
        distinctUntilChanged(
          (prev, curr) => prev[0] === curr[0] && prev[1] === curr[1]
        ),
        switchMap(([product, industry]) =>
          this.loadFormAdminSchema(product, industry)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ schema, sid }) => {
        this.extractDropdownOptions(schema, sid);
      });
  }

  private loadFormAdminSchema(productId: string, industryId: string) {
    const sid: Record<string, string> = {
      a: SID_AGENCY,
      c: SID_COMMON,
      f: SID_FEES,
      b: SID_BORROWER,
      c1: SID_COLLATERAL,
      g: SID_GUARANTORS,
      l: productId,
      s: industryId,
      p: SID_PARTICIPATION,
    };
    let params = new HttpParams();
    for (const schema of Object.values(sid)) {
      const prop = schema.split('.').reverse()[1];
      params = params.append(prop, schema);
    }
    const query = `io.synply.v1.view.form_admin?${params}`;

    this.schemasStore.loadByQuery(query);
    const schemaSignal = this.schemasStore.schemaByQuery(query);
    return toObservable(schemaSignal, { injector: this.injector }).pipe(
      filter(schema => !!schema),
      first(),
      map(schema => ({ schema, sid }))
    );
  }

  private extractDropdownOptions(
    schema: any,
    sid: Record<string, string>
  ): void {
    if (!schema?.properties) return;

    const namespaceMap = Object.entries(sid).reduce(
      (acc, [key, value]) => ({ ...acc, [value]: key }),
      {} as Record<string, string>
    );

    const propertiesList = (schema.properties as any[]).map(cur => {
      const rawId: string = cur.id ?? '';
      const dot = rawId.lastIndexOf('.');
      const normalizedId =
        dot >= 0
          ? `${namespaceMap[rawId.slice(0, dot)] ?? rawId.slice(0, dot)}.${rawId.slice(dot + 1)}`
          : rawId;
      return { ...cur, normalizedId };
    });

    const dropdownKeys = [
      'c.pmtFrq',
      'c.indx',
      'c.rrf',
      'c.sTp',
      'c.uTp',
      'c.loanPurpose',
      'c.usState',
      'c.ddConv',
      'c.expectedUtilization',
      'c.crePropertyClass',
      'c.projectType',
      'b.borrowerRevenueSize',
      'b.privateEquitySponsored',
      'f.interestRateType',
      'f.interestRateFloorType',
      'f.interestRateHedge',
      'f.performanceBasedPricing',
      'f.prepaymentPenaltyFee',
      'c1.recourseLevel',
      'g.guarantorLiabilityBurndown',
    ];

    const requests = dropdownKeys.map(fieldKey => {
      const prop = propertiesList.find(p => p.normalizedId === fieldKey);
      const raw =
        prop?.v ?? prop?.input?.v ?? prop?.input?.vals ?? prop?.input?.values;
      const values = this.extractEnumValues(raw);

      if (values.length === 0) {
        return of([fieldKey, []] as [string, SchemaEnumItem[]]);
      }
      return this.mapToSchemaItems(values).pipe(
        map(options => [fieldKey, options] as [string, SchemaEnumItem[]])
      );
    });

    forkJoin(requests)
      .pipe(take(1))
      .subscribe(entries => {
        this.termSheetSelectOptions.set(
          entries.reduce(
            (acc, [key, opts]) => ({ ...acc, [key]: opts }),
            {} as Record<string, SchemaEnumItem[]>
          )
        );
        this.cdr.markForCheck();
      });
  }

  private extractEnumValues(raw: unknown): SchemaEnumItem[] {
    if (Array.isArray(raw)) return raw as SchemaEnumItem[];
    if (raw && typeof raw === 'object') {
      const vals = (raw as any).vals ?? (raw as any).values;
      if (vals && typeof vals === 'object') {
        return Object.entries(vals).map(([id, value]) => ({
          id,
          ...(typeof value === 'object' ? value : { name: value }),
        })) as SchemaEnumItem[];
      }
    }
    return [];
  }

  private mapToSchemaItems(values: SchemaEnumItem[]) {
    const options = values.map(opt => ({
      id: String((opt as any).id ?? (opt as any).value ?? opt),
      label: (opt as any).label as string | undefined,
      description: (opt as any).description as string | undefined,
      name: (opt as any).name as string | undefined,
    }));

    const labels = options.map(o => o.label).filter(Boolean);

    if (labels.length === 0) {
      return of(
        options.map(o => ({
          ...o,
          name: o.name || o.description || o.label || o.id,
        }))
      );
    }

    return this.translateService.getTranslations(labels).pipe(
      take(1),
      map(translations =>
        options.map(o => ({
          ...o,
          name:
            translations[o.label] || o.name || o.description || o.label || o.id,
        }))
      )
    );
  }
}
